import React, { useEffect, useState } from 'react';
import { Check, Clock, Shield, Users, Award, Zap, FileText, Trophy, Download, Lock } from 'lucide-react';
import { MetaPixel } from './components/MetaPixel';
import { CTAButton } from './components/CTAButton';
import { PurchasePopup } from './components/PurchasePopup';
import { BonusCard } from './components/BonusCard';
import { TestimonialCard } from './components/TestimonialCard';
import { FAQItem } from './components/FAQItem';
import { MaterialCarousel } from './components/MaterialCarousel';
export function App() {
  // Image URLs
  const mainImage = 'https://cdn.magicpatterns.com/uploads/7RXwoymgBj3gSNNbDE9icg/Diseno-sin-titulo-_55_.webp';
  const bonus1Image = 'https://cdn.magicpatterns.com/uploads/6FZppY7hxbrSZPGKbc4uWo/14.png';
  const bonus2Image = 'https://cdn.magicpatterns.com/uploads/rqx1MPTvbzYGSqGwQHthbP/15.png';
  const bonus3Image = 'https://cdn.magicpatterns.com/uploads/d7gvQZDspv94fEMwmGGG6y/16.png';
  const bonus4Image = 'https://cdn.magicpatterns.com/uploads/bVkeiqNrLYDQ8aw5kqStUy/17.png';
  const bonus5Image = 'https://cdn.magicpatterns.com/uploads/rwCqUkSXZh23aibfAjnFUc/18.png';
  const bonus6Image = 'https://cdn.magicpatterns.com/uploads/67U1L6SrMjmUjDCyamRJVh/19.png';
  // Material preview images for carousel (volley content)
  const materialPreviewImages = ['https://cdn.magicpatterns.com/uploads/xzninLGAwzZjvpufNtxMt4/Captura-de-pantalla-2026-05-23-a-las-2.07.38_p.-m..webp', 'https://cdn.magicpatterns.com/uploads/5AeWjjUDy3eifmzoYjBspY/Captura-de-pantalla-2026-05-23-a-las-2.08.20_p.-m..webp', 'https://cdn.magicpatterns.com/uploads/cHLx8fnAMdCtQFEfmepiGJ/Captura-de-pantalla-2026-05-23-a-las-2.08.45_p.-m..webp', 'https://cdn.magicpatterns.com/uploads/7RAr49CTHeD1JqEGrEe2zh/Captura-de-pantalla-2026-05-23-a-las-2.09.08_p.-m..webp', 'https://cdn.magicpatterns.com/uploads/2vJK9RtRXW9Mx9r1W4i5bJ/Captura-de-pantalla-2026-05-23-a-las-2.13.39_p.-m..webp'];
  // Countdown Timer
  const [timeLeft, setTimeLeft] = useState({
    hours: 0,
    minutes: 14,
    seconds: 54
  });
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) {
          return {
            ...prev,
            seconds: prev.seconds - 1
          };
        } else if (prev.minutes > 0) {
          return {
            ...prev,
            minutes: prev.minutes - 1,
            seconds: 59
          };
        } else if (prev.hours > 0) {
          return {
            ...prev,
            hours: prev.hours - 1,
            minutes: 59,
            seconds: 59
          };
        } else {
          return {
            hours: 0,
            minutes: 14,
            seconds: 54
          };
        }
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);
  const formatTime = (num: number) => num.toString().padStart(2, '0');
  return <div className="min-h-screen bg-white font-sans text-gray-900" data-id="element-0">
      <MetaPixel data-id="element-1" />
      <PurchasePopup data-id="element-2" />

      {/* 1. Top Bar */}
      <div className="bg-blue-950 text-white py-2 text-center text-sm font-bold sticky top-0 z-50 shadow-md" data-id="element-3">
        <div className="flex items-center justify-center gap-2" data-id="element-4">
          <Clock className="h-4 w-4 text-yellow-400" data-id="element-5" />
          <span data-id="element-6">
            L'offre se termine dans : {formatTime(timeLeft.hours)}:
            {formatTime(timeLeft.minutes)}:{formatTime(timeLeft.seconds)}
          </span>
        </div>
      </div>

      {/* 2. Hero Section */}
      <header className="bg-white pt-6 pb-16 px-4" data-id="element-7">
        <div className="max-w-4xl mx-auto text-center" data-id="element-8">
          <div className="inline-flex items-center gap-1.5 text-blue-900 font-bold uppercase tracking-wider text-[10px] md:text-xs mb-4 border border-blue-100 bg-blue-50 px-3 py-0.5 rounded-full" data-id="element-9">
            <Trophy className="h-3 w-3 text-yellow-500" data-id="element-10" />
            SPÉCIAL ENTRAÎNEURS ET PROFS DE VOLLEY
          </div>

          <h1 className="text-2xl md:text-4xl font-extrabold text-gray-900 mb-4 leading-tight" data-id="element-11">
            Volley Clé en Main :{' '}
            <span className="text-blue-900 bg-yellow-200/60 px-1 rounded" data-id="element-12">
              Votre saison complète, 100% planifiée.
            </span>
          </h1>

          <div className="max-w-[280px] mx-auto mb-4" data-id="element-13">
            <img src={mainImage} alt="Volley Clé en Main" className="w-full h-auto transform hover:scale-105 transition-transform duration-500" fetchPriority="high" decoding="async" data-id="element-14" />
          </div>

          <div className="flex items-center justify-center gap-2 text-blue-900 font-bold text-lg mb-2" data-id="element-15"></div>

          <p className="text-base md:text-lg text-gray-600 mb-6 max-w-2xl mx-auto" data-id="element-16">
            Le système complet de séances, exercices et fiches tactiques prêts à
            l'emploi.{' '}
            <span className="font-bold text-gray-900" data-id="element-17">
              Toute votre saison résolue (Tous niveaux)
            </span>
          </p>

          <div className="flex flex-col items-center gap-4" data-id="element-18">
            <CTAButton size="xl" scrollToId="pricing" data-id="element-19">
              👉 OUI, JE VEUX UN ACCÈS IMMÉDIAT
            </CTAButton>

            <div className="flex flex-wrap justify-center gap-4 text-xs text-gray-500 mt-2" data-id="element-20">
              <span className="flex items-center gap-1" data-id="element-21">
                <Lock className="h-3 w-3" data-id="element-22" /> Paiement sécurisé
              </span>
              <span className="flex items-center gap-1" data-id="element-23">
                <Download className="h-3 w-3" data-id="element-24" /> Téléchargement immédiat
              </span>
              <span className="flex items-center gap-1" data-id="element-25">
                <Shield className="h-3 w-3" data-id="element-26" /> Garantie 7 jours
              </span>
              <span className="flex items-center gap-1" data-id="element-27">
                <Check className="h-3 w-3" data-id="element-28" /> Accès à vie
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* 3. Problem Section */}
      <section className="py-16 bg-gray-50 px-4" data-id="element-29">
        <div className="max-w-3xl mx-auto" data-id="element-30">
          <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-900 mb-10" data-id="element-31">
            Si vous entraînez du Volley, cela va vous{' '}
            <span className="text-blue-900" data-id="element-32">sembler familier...</span>
          </h2>

          <div className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 mb-8" data-id="element-33">
            <p className="text-lg text-gray-700 mb-6 leading-relaxed" data-id="element-34">
              Vous rentrez du travail <span className="font-bold" data-id="element-35">épuisé</span>{' '}
              et vous devez encore préparer la séance du soir. Des heures à
              chercher des exercices sur internet, dessiner des schémas
              tactiques, essayer d'inventer quelque chose de nouveau pour ne pas
              lasser vos joueurs...
            </p>
            <p className="text-lg text-gray-700 font-medium" data-id="element-36">
              Ce n'est pas un manque de compétence. C'est qu'il vous manque une
              méthode structurée qui vous soutient toute l'année.
            </p>
          </div>
        </div>
      </section>

      {/* 4. Solution Section */}
      <section className="py-16 bg-white px-4 text-center" data-id="element-37">
        <div className="max-w-4xl mx-auto" data-id="element-38">
          <span className="text-blue-900 font-bold tracking-wider uppercase text-sm mb-2 block" data-id="element-39">
            La Solution
          </span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4" data-id="element-40">
            LA COLLECTION VOLLEY CLÉ EN MAIN™
          </h2>
          <p className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto" data-id="element-41">
            La méthode complète de projets pédagogiques, entraînements et
            progressions déjà testés sur le terrain.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-42">
            {[{
            icon: Check,
            text: "100% Prêt à l'emploi"
          }, {
            icon: Trophy,
            text: 'Testé sur le terrain'
          }, {
            icon: FileText,
            text: 'Structure pro'
          }, {
            icon: Zap,
            text: 'Gain de temps'
          }].map((item, i) => <div key={i} className="flex flex-col items-center p-4 bg-blue-50 rounded-xl" data-id="element-43">
                <div className="bg-white p-3 rounded-full shadow-sm mb-3" data-id="element-44">
                  <item.icon className="h-6 w-6 text-blue-900" data-id="element-45" />
                </div>
                <span className="font-bold text-gray-800 text-sm md:text-base" data-id="element-46">
                  {item.text}
                </span>
              </div>)}
          </div>
        </div>
      </section>

      {/* 5. What Includes Section */}
      <section className="py-16 bg-gray-50 px-4" data-id="element-47">
        <div className="max-w-5xl mx-auto" data-id="element-48">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12" data-id="element-49">
            Que contient cette collection ?
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-50">
            {['6 Tomes de référence (École de volley aux Seniors)', "60+ Fiches d'exercices détaillées", 'Planifications de saison complètes', "Feuilles de match et grilles d'évaluation", 'Schémas de jeu et rotations tactiques', 'Outils de gestion et leadership pour coachs'].map((item, i) => <div key={i} className="flex items-center p-4 bg-white rounded-xl shadow-sm border border-gray-100" data-id="element-51">
                <div className="bg-blue-100 p-2 rounded-full mr-4 flex-shrink-0" data-id="element-52">
                  <Check className="h-5 w-5 text-blue-900" data-id="element-53" />
                </div>
                <span className="font-medium text-gray-800" data-id="element-54">{item}</span>
              </div>)}
          </div>
        </div>
      </section>

      {/* 6. Inside Look Section */}
      <section className="py-16 bg-white px-4 text-center" data-id="element-55">
        <div className="max-w-4xl mx-auto" data-id="element-56">
          <h2 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-57">
            APERÇU DU CONTENU
          </h2>
          <p className="text-xl text-blue-900 font-medium mb-4" data-id="element-58">
            Pages réelles de la collection
          </p>
          <p className="text-gray-600 mb-8" data-id="element-59">
            Des séances étape par étape, des entraînements complets et des
            exercices pratiques
          </p>

          <MaterialCarousel images={materialPreviewImages} data-id="element-60" />
        </div>
      </section>

      {/* 7. Results Section */}
      <section className="py-16 bg-gray-50 px-4" data-id="element-61">
        <div className="max-w-5xl mx-auto" data-id="element-62">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12" data-id="element-63">
            Des résultats concrets
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-64">
            {/* Players */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border-t-4 border-blue-900" data-id="element-65">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-66">
                <Users className="h-6 w-6 text-blue-900 mr-2" data-id="element-67" />
                Pour vos Joueurs
              </h3>
              <ul className="space-y-4" data-id="element-68">
                {['Une progression constante grâce à des séances logiques', 'Une meilleure compréhension des systèmes tactiques', 'Un engagement renforcé grâce à des exercices variés', "Le plaisir de s'entraîner avec du matériel pro"].map((item, i) => <li key={i} className="flex items-start" data-id="element-69">
                    <Check className="h-5 w-5 text-blue-900 mr-3 mt-0.5 flex-shrink-0" data-id="element-70" />
                    <span className="text-gray-700" data-id="element-71">{item}</span>
                  </li>)}
              </ul>
            </div>

            {/* Coach */}
            <div className="bg-white p-8 rounded-2xl shadow-sm border-t-4 border-yellow-400" data-id="element-72">
              <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-73">
                <Award className="h-6 w-6 text-yellow-500 mr-2" data-id="element-74" />
                Pour Vous (L'Entraîneur)
              </h3>
              <ul className="space-y-4" data-id="element-75">
                {['Éliminez le stress de la préparation de dernière minute', 'Coachez avec une autorité et une clarté renforcées', 'Retrouvez du temps libre pour vous et votre famille', 'Gérez vos effectifs comme un pro'].map((item, i) => <li key={i} className="flex items-start" data-id="element-76">
                    <Check className="h-5 w-5 text-yellow-500 mr-3 mt-0.5 flex-shrink-0" data-id="element-77" />
                    <span className="text-gray-700" data-id="element-78">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>

          <div className="mt-12 text-center" data-id="element-79">
            <CTAButton size="xl" scrollToId="pricing" data-id="element-80">
              👉 JE VEUX RÉUSSIR MA SAISON
            </CTAButton>
          </div>
        </div>
      </section>

      {/* 8. Bonuses Section */}
      <section className="py-16 bg-white px-4" data-id="element-81">
        <div className="max-w-6xl mx-auto" data-id="element-82">
          <div className="text-center mb-12" data-id="element-83">
            <h2 className="text-3xl font-bold text-gray-900 mb-2" data-id="element-84">
              🎁 6 COMPLÉMENTS OFFERTS AUJOURD'HUI
            </h2>
            <p className="text-xl text-blue-900 font-medium" data-id="element-85">
              Inclus gratuitement avec votre commande
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-86">
            <BonusCard bonusNumber={1} title="Le Kit de Scouting Simplifié" description="Tous les outils pour analyser vos adversaires et préparer vos matchs efficacement." imageSrc={bonus1Image} value={15} data-id="element-87" />
            <BonusCard bonusNumber={2} title="L'Agenda Stratégique du Coach" description="Planifiez votre saison, vos séances et vos objectifs sans rien laisser au hasard." imageSrc={bonus2Image} value={15} data-id="element-88" />
            <BonusCard bonusNumber={3} title="Guide de la Discipline et Cohésion d'Équipe" description="Des protocoles éprouvés pour souder votre groupe et installer un cadre solide." imageSrc={bonus3Image} value={15} data-id="element-89" />
            <BonusCard bonusNumber={4} title="Carnet des Tests de Performance Physique" description="Évaluez et suivez la progression physique de chaque joueur tout au long de la saison." imageSrc={bonus4Image} value={15} data-id="element-90" />
            <BonusCard bonusNumber={5} title="Diplômes et Certificats MVP pour vos joueurs" description="Récompensez vos joueurs avec des supports professionnels prêts à imprimer." imageSrc={bonus5Image} value={10} data-id="element-91" />
            <BonusCard bonusNumber={6} title="Protocole de Prévention des Blessures" description="Échauffements, étirements et routines clés pour garder votre équipe sur le terrain." imageSrc={bonus6Image} value={15} data-id="element-92" />
          </div>
        </div>
      </section>

      {/* 9. Pricing Section */}
      <section id="pricing" className="py-6 md:py-16 bg-blue-950 px-4 relative overflow-hidden scroll-mt-10" data-id="element-93">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10" data-id="element-94">
          <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" data-id="element-95"></div>
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-yellow-400 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" data-id="element-96"></div>
        </div>

        <div className="max-w-md mx-auto relative z-10" data-id="element-97">
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden" data-id="element-98">
            <div className="bg-red-600 text-white text-center py-2 font-bold text-sm uppercase tracking-wide animate-pulse" data-id="element-99">
              🔥 OFFRE SPÉCIALE DE LANCEMENT
            </div>

            <div className="p-5 md:p-8 text-center" data-id="element-100">
              <h3 className="text-xl md:text-2xl font-black text-gray-900 mb-1" data-id="element-101">
                VOLLEY CLÉ EN MAIN
              </h3>
              <p className="text-blue-900 font-bold mb-4" data-id="element-102">
                Collection de 6 Volumes + 6 Compléments
              </p>

              <div className="space-y-2 mb-5 text-left" data-id="element-103">
                {['Paiement unique', 'Accès à vie', 'Téléchargement immédiat', 'Garantie 7 jours', 'Tous les bonus inclus'].map((item, i) => <div key={i} className="flex items-center" data-id="element-104">
                    <Check className="h-4 w-4 text-blue-900 mr-3 flex-shrink-0" data-id="element-105" />
                    <span className="text-gray-700 text-sm" data-id="element-106">{item}</span>
                  </div>)}
              </div>

              <div className="mb-1" data-id="element-107">
                <span className="text-gray-400 line-through text-base" data-id="element-108">
                  Prix habituel 49,99 €
                </span>
              </div>
              <div className="flex items-center justify-center gap-2 mb-1 pt-[5px] pb-[5px]" data-id="element-109">
                <span className="text-5xl md:text-6xl font-black text-gray-900" data-id="element-110">
                  17,00
                </span>
                <span className="font-bold text-gray-500 self-end mb-1 text-[28px]" data-id="element-111">
                  €
                </span>
              </div>

              <CTAButton fullWidth size="xl" href="https://france.edulabs.online/cart/43444134412406:1" data-id="element-112">
                👉 OUI, JE VEUX LE PACK
              </CTAButton>

              {/* Trust Block */}
              <div className="mt-4 space-y-3" data-id="element-113">
                {/* Star rating + social proof */}
                <div className="flex items-center justify-center gap-2 text-sm" data-id="element-114">
                  <span className="text-yellow-400 tracking-tight" data-id="element-115">★★★★★</span>
                  <span className="text-gray-700 font-semibold" data-id="element-116">4,9/5</span>
                  <span className="text-gray-500" data-id="element-117">
                    · Plus de 1 200 coachs satisfaits
                  </span>
                </div>

                {/* Security + Guarantee badges */}
                <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-[11px] text-gray-600 font-medium" data-id="element-118">
                  <span className="flex items-center gap-1" data-id="element-119">
                    <Lock className="h-3 w-3 text-emerald-600" data-id="element-120" />
                    Paiement 100% sécurisé
                  </span>
                  <span className="flex items-center gap-1" data-id="element-121">
                    <Shield className="h-3 w-3 text-emerald-600" data-id="element-122" />
                    Garantie 7 jours
                  </span>
                  <span className="flex items-center gap-1" data-id="element-123">
                    <Download className="h-3 w-3 text-emerald-600" data-id="element-124" />
                    Accès immédiat
                  </span>
                </div>

                {/* Payment methods */}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 10. Guarantee Section */}
      <section className="py-16 bg-white px-4 text-center" data-id="element-125">
        <div className="max-w-2xl mx-auto" data-id="element-126">
          <div className="inline-block p-4 bg-blue-100 rounded-full mb-6" data-id="element-127">
            <Shield className="h-12 w-12 text-blue-900" data-id="element-128" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-129">
            Garantie de Satisfaction Totale
          </h2>
          <p className="text-xl text-gray-600" data-id="element-130">
            Testez-le pendant 7 jours. Si la méthode ne change pas radicalement
            votre façon d'entraîner, nous vous remboursons 100 %. Sans poser de
            questions.
          </p>
        </div>
      </section>

      {/* 11. Testimonials Section */}
      <section className="py-16 bg-gray-50 px-4" data-id="element-131">
        <div className="max-w-6xl mx-auto" data-id="element-132">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12" data-id="element-133">
            Témoignages d'Entraîneurs
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-134">
            <TestimonialCard name="Jean-Marc L." role="Coach M15" location="Lyon, France" content="Je préparais mes séances au feeling. Maintenant j'ai une structure pro qui me fait gagner un temps fou." initials="JL" data-id="element-135" />
            <TestimonialCard name="Amélie D." role="Prof d'EPS" location="Paris, France" content="Le contenu est visuel et très facile à mettre en place. Mes élèves adorent la variété des exercices." initials="AD" data-id="element-136" />
            <TestimonialCard name="Marc R." role="Entraîneur Senior" location="Marseille, France" content="Idéal pour éviter la routine. Une ressource indispensable pour tout club qui veut progresser." initials="MR" data-id="element-137" />
          </div>
        </div>
      </section>

      {/* 12. FAQ Section */}
      <section className="py-16 bg-white px-4" data-id="element-138">
        <div className="max-w-3xl mx-auto" data-id="element-139">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12" data-id="element-140">
            Questions Fréquentes
          </h2>

          <div className="space-y-2" data-id="element-141">
            <FAQItem question="Sous quel format est livré le contenu ?" answer="Tout est au format PDF haute résolution, lisible sur smartphone, tablette et ordinateur." data-id="element-142" />
            <FAQItem question="Est-ce que je peux imprimer les documents ?" answer="Oui, absolument. Le format est optimisé pour être imprimé et emporté directement sur le terrain." data-id="element-143" />
            <FAQItem question="Quels niveaux sont couverts ?" answer="La collection couvre tous les cycles : du volley débutant (M11/M13) jusqu'aux systèmes tactiques seniors." data-id="element-144" />
            <FAQItem question="Quand vais-je recevoir mon accès ?" answer="C'est instantané. Dès la validation du paiement, vous recevez un email avec vos accès de téléchargement." data-id="element-145" />
          </div>
        </div>
      </section>

      {/* 13. Final CTA */}
      <section className="py-20 bg-blue-950 text-white px-4 text-center" data-id="element-146">
        <div className="max-w-4xl mx-auto" data-id="element-147">
          <h2 className="text-3xl md:text-5xl font-extrabold mb-6" data-id="element-148">
            Prêt à transformer{' '}
            <span className="text-yellow-400" data-id="element-149">vos entraînements ?</span>
          </h2>
          <p className="text-xl md:text-2xl text-blue-100 mb-10" data-id="element-150">
            Rejoignez les coachs qui ont choisi de professionnaliser leur
            pratique tout en protégeant leur temps libre
          </p>
          <div className="flex justify-center" data-id="element-151">
            <CTAButton size="xl" scrollToId="pricing" data-id="element-152">
              👉 ACCÉDER À LA COLLECTION MAINTENANT
            </CTAButton>
          </div>
        </div>
      </section>

      {/* 14. Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12 px-4 text-center border-t border-gray-800" data-id="element-153">
        <div className="max-w-6xl mx-auto" data-id="element-154">
          <p className="mb-4 font-bold text-white tracking-wider" data-id="element-155">
            VOLLEY CLÉ EN MAIN™
          </p>
          <p className="text-sm mb-6" data-id="element-156">
            © 2026 Volley Clé en Main™. Tous droits réservés.
          </p>
          <div className="flex justify-center space-x-6 text-sm" data-id="element-157">
            <span className="hover:text-white cursor-pointer transition-colors" data-id="element-158">
              Mentions Légales
            </span>
            <span className="hover:text-white cursor-pointer transition-colors" data-id="element-159">
              Confidentialité
            </span>
            <span className="hover:text-white cursor-pointer transition-colors" data-id="element-160">
              Contact
            </span>
          </div>
        </div>
      </footer>
    </div>;
}